import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleAlert } from "lucide-react";

import api from "../../axios/axiosConfig";
import { useBrandContext } from "../../context/BrandContext";
import { useToast } from "../../context/ToastModalContext";

import Button from "../../components/button/Button";
import Input from "../../components/input/Input";
import Modal from "../../components/modal/Modal";

import {
  addCreditBalance,
  getUserPaymentMethodById,
} from "../../services/userPaymentService";

import addCircle from "../../assets/add_circle_fill.svg";
import checkGreen from "../../assets/payments/check_circle-green.svg";
import couponIcon from "../../assets/icon_coupon.png";
import { ICreditCard } from "../../shared/models/creditCard/creditCard";
import { getTypeCard } from "../../utils/format";
import { isWalletAllowed } from "../payment/creditCardList/CreditCardList";

import "./AddCredit.css";
import { useHeaderContext } from "../../context/HeaderContext";
import { useAuth } from "../../context/AuthContext";
import { getUser } from "../../services/userService";
import { hasBillingAddress } from "../../utils/billingAddress";
import { ReactComponent as LoaderSecondary } from "../../assets/SpinnerBlack.svg";

const AddCredit: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCardInfo, setIsLoadingCardInfo] = useState(false);
  const [cardInfo, setCardInfo] = useState<ICreditCard>();
  const navigate = useNavigate();
  const { currenCreditCardId } = useParams();
  const brandContext = useBrandContext();
  const { setHeaderConfig } = useHeaderContext();
  
  // Obter configuração de wallet do BrandContext
  const walletConfig = brandContext.brandData?.appWalletTopUpConfig || {
    maxValue: 1000,
    minValueCard: 35,
    minValuePix: 10,
    defaultValue: 35,
    presetValues: [35, 60, 100, 150, 300],
    allowCustomValue: true,
  };

  // Determinar se é PIX ou Cartão de Crédito
  const isPixPayment = currenCreditCardId === "pix";

  // Extrair valores de configuração
  const presetValues = walletConfig.presetValues || [35, 60, 100, 150, 300];
  const defaultValue = walletConfig.defaultValue || presetValues[0] || 35;
  const maxValue = walletConfig.maxValue || 1000;
  
  // Usar minValue específico baseado no método de pagamento
  // Suporta configuração antiga (minValue) e nova (minValueCard/minValuePix)
  const minValue = isPixPayment
    ? (walletConfig.minValuePix ?? walletConfig.minValue ?? 10)
    : (walletConfig.minValueCard ?? walletConfig.minValue ?? 35);
  
  const allowCustomValue = walletConfig.allowCustomValue !== false; // true por padrão

  // Inicializar com defaultValue
  const [selectedValue, setSelectedValue] = useState<number | string>(defaultValue);
  
  const [cashAmountsCoupons, setCashAmountsCoupons] = useState([]);
  const [selectedCouponId, setSelectedCouponId] = useState();
  const [otherValue, setOtherValue] = useState<string | null>(null);
  const [errorsValidate, setErrorsValidate] = useState<{ otherValue: boolean }>(
    { otherValue: false }
  );
  const { user: authUser, setUser: setAuthUser, isLogged } = useAuth();
  const [userInfo, setUserInfo] = useState<any | null>(authUser ?? null);
  const [isCheckingCpf, setIsCheckingCpf] = useState(true);
  const [hasRedirectedForCpf, setHasRedirectedForCpf] = useState(false);
  const [hasRedirectedForAddress, setHasRedirectedForAddress] = useState(false);

  const [showCouponInfoModal, setShowCouponInfoModal] =
    useState<boolean>(false);
  const [titleCouponInfoModal, setTitleCouponInfoModal] = useState<string>("");
  const [textCouponInfoModal, setTextCouponInfoModal] = useState<string>("");

  const fetchSalesCoupons = async () => {
    try {
      const response = await api.get(`/DiscountCoupon/SalesCoupon`);
      const vouchers = response.data ?? [];
      const vouchersValues = vouchers.map((item: any) => {
        return {
          dcValueVoucher: item.dcValueVoucher,
          dcParameter: item.dcParameter,
          dcCode: item.dcCode,
          dcId: item.dcId,
        };
      });
      setCashAmountsCoupons(vouchersValues as []);
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleCouponDetail = (couponInfo: any) => {
    setTitleCouponInfoModal(
      `Adicione R$ ${new Intl.NumberFormat(String("pt"), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        couponInfo.dcValueVoucher
      )} de crédito e ganhe ${new Intl.NumberFormat(String("pt"), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(
        couponInfo.dcParameter
      )}% de desconto no valor das recargas utilizando este crédito.`
    );
    setTextCouponInfoModal(
      `Ao adicionar R$ ${new Intl.NumberFormat(String("pt"), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(
        couponInfo.dcValueVoucher
      )} de crédito, você receberá um cupom de desconto de ${new Intl.NumberFormat(
        String("pt"),
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      ).format(
        couponInfo.dcParameter
      )}% válido para as próximas recargas na rede, até o valor total de R$ ${new Intl.NumberFormat(
        String("pt"),
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(couponInfo.dcValueVoucher)}.`
    );
    setShowCouponInfoModal(true);
  };

  const { showToast } = useToast();
  const isCpfMissing = !userInfo?.cpf;
  // Credito no cartao exige endereco de cobranca; o Pix nao.
  const isAddressMissing = !isPixPayment && !hasBillingAddress(userInfo);

  // Atualizar dados da marca quando a tela for acessada
  useEffect(() => {
    console.log('AddCredit page accessed - refreshing brand data');
    brandContext.refreshBrandData?.();
  }, []);

  // Ajustar valor selecionado quando configuração mudar
  useEffect(() => {
    console.log('Wallet TopUp Config loaded:', {
      paymentMethod: isPixPayment ? 'PIX' : 'Cartão de Crédito',
      presetValues: presetValues,
      defaultValue: defaultValue,
      minValue: minValue,
      minValueCard: walletConfig.minValueCard,
      minValuePix: walletConfig.minValuePix,
      maxValue: maxValue,
      allowCustomValue: allowCustomValue,
    });
    
    // Se o valor selecionado não está no novo array, usar o valor padrão
    if (!presetValues.includes(selectedValue as number)) {
      setSelectedValue(defaultValue);
    }
  }, [presetValues, defaultValue, minValue, maxValue, allowCustomValue, isPixPayment]);

  useEffect(() => {
    let isMounted = true;

    const loadUserInfo = async () => {
      if (authUser) {
        if (!isMounted) return;
        setUserInfo(authUser);
        setIsCheckingCpf(false);
        return;
      }

      // Sem sessao nao ha o que buscar: a requisicao sairia sem token e
      // tomaria 401.
      if (!isLogged()) {
        if (!isMounted) return;
        setUserInfo(null);
        setIsCheckingCpf(false);
        return;
      }

      if (!isMounted) return;
      setIsCheckingCpf(true);
      try {
        const response = await getUser();
        if (!isMounted) return;
        setUserInfo(response);
        setAuthUser(response);
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setUserInfo(null);
      } finally {
        if (isMounted) {
          setIsCheckingCpf(false);
        }
      }
    };

    loadUserInfo();

    return () => {
      isMounted = false;
    };
  }, [authUser, setAuthUser]);

  useEffect(() => {
    if (isCheckingCpf || hasRedirectedForCpf) return;
    if (!userInfo?.cpf) {
      setHasRedirectedForCpf(true);
      showToast({
        title: "CPF necessário",
        messageModal:
          "É necessário atualizar o seu CPF nas suas informações pessoais.",
        textEnterButton: "Ir para o perfil",
        allowButtonClosed: true,
        handleEnterButton: () =>
          navigate(`/${brandContext.brandName}/userProfile`, {
            state: { cpfRequired: true },
          }),
        closeButtonText: "Fechar",
      });
      navigate(`/${brandContext.brandName}/userProfile`, {
        state: { cpfRequired: true },
        replace: true,
      });
    }
  }, [
    brandContext.brandName,
    hasRedirectedForCpf,
    isCheckingCpf,
    navigate,
    showToast,
    userInfo?.cpf,
  ]);

  // Depois do CPF, e so para cartao: um aviso por vez, e o do CPF ja tira o
  // usuario da tela. Ao salvar o perfil ele volta para este mesmo cartao.
  useEffect(() => {
    if (isCheckingCpf || hasRedirectedForCpf || hasRedirectedForAddress) return;
    if (!userInfo?.cpf || !isAddressMissing) return;

    setHasRedirectedForAddress(true);
    showToast({
      title: "Endereço necessário",
      messageModal:
        "Para adicionar crédito com cartão, precisamos do seu endereço de cobrança. Preencha no seu perfil e você volta para cá.",
      allowButtonClosed: true,
      closeButtonText: "OK",
    });
    navigate(`/${brandContext.brandName}/userProfile`, {
      state: {
        addressRequired: true,
        returnTo: `/${brandContext.brandName}/addCredit/${currenCreditCardId}`,
      },
      replace: true,
    });
  }, [
    brandContext.brandName,
    currenCreditCardId,
    hasRedirectedForAddress,
    hasRedirectedForCpf,
    isAddressMissing,
    isCheckingCpf,
    navigate,
    showToast,
    userInfo?.cpf,
  ]);

  const handleConfirmCredit = async () => {
    if (isCpfMissing || isAddressMissing) {
      return;
    }

    // Mesma regra da lista de cartoes, para quem chega aqui pela URL direto.
    if (!isPixPayment && !isWalletAllowed(cardInfo)) {
      showToast({
        title: "Cartão indisponível",
        messageModal:
          "Este cartão não pode ser usado para adicionar crédito. Escolha outro cartão ou pague com Pix.",
        allowButtonClosed: true,
        closeButtonText: "Entendi",
      });
      return;
    }
    setIsLoading(true);
    let toastTitle = "Falha";
    let toastSubTitle =
      "Não foi possível completar a solicitação. Por favor tente novamente.";
    let toastButtonClose = "Fechar";
    try {
      let amount: number;
      if (selectedValue === "other" && otherValue) {
        const numeric = otherValue
          .replace(/[^\d,.-]/g, "")
          .replace(".", "")
          .replace(",", ".");
        amount = parseFloat(numeric);
      } else {
        amount = selectedValue as number;
      }
      const response = await addCreditBalance(
        amount,
        cardInfo?.id ? cardInfo?.id : currenCreditCardId!,
        selectedCouponId
      );
      setIsLoading(false);
      if (response?.status === 200 && currenCreditCardId === "pix") {
        navigate(
          `/${brandContext.brandName}/addPix/${response?.data?.orderId}`
        );
        return;
      }
      if (response?.status === 200) {
        const orderData = response.data;
        if (orderData?.orderState === "paid") {
          return;
        } else if (
          orderData?.orderState === "captured" ||
          orderData?.orderState === "pending"
        ) {
          toastTitle = "Processando crédito...";
          toastSubTitle =
            "Precisamos de um tempo para processar seu pagamento. Nós te avisaremos quando o valor for creditado.";
          toastButtonClose = "Entendi";
        } else if (
          orderData?.orderState === "unauthorized" ||
          orderData?.orderState === "canceled"
        ) {
          toastTitle = "Não autorizado";
          toastSubTitle = "A transação no cartão de crédito foi negada";
          toastButtonClose = "Fechar";
        } else if (orderData?.orderState === "error") {
          toastTitle = "Falha";
          toastSubTitle =
            "Erro ao completar a transação. Por favor tente novamente.";
          toastButtonClose = "Fechar";
        } else {
          return;
        }
      }
    } catch (er) {
      setIsLoading(false);

      console.log(er);
    }

    showToast({
      title: toastTitle,
      messageModal: toastSubTitle,
      allowButtonClosed: true,
      closeButtonText: toastButtonClose,
    });
  };

  const handleToastConfirmCredid = () => {
    if (isCpfMissing || isAddressMissing) {
      return;
    }
    let amount: number;
    if (selectedValue === "other") {
      if (!otherValue) {
        setErrorsValidate({ otherValue: true });
        return;
      }
      const numeric = otherValue
        .replace(/[^\d,.-]/g, "")
        .replace(".", "")
        .replace(",", ".");
      amount = parseFloat(numeric);
      if (!amount || isNaN(amount) || amount < minValue || amount > maxValue) {
        setErrorsValidate({ otherValue: true });
        return;
      }
      setErrorsValidate({ otherValue: false });
    } else {
      amount = selectedValue as number;
    }

    showToast({
      title: `Confirmar crédito de ${new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)}`,
      messageModal: `Você está adicionando ${new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)} de créditos. Você confirma?`,
      textEnterButton: "Confirmar",
      allowButtonClosed: true,
      handleEnterButton: handleConfirmCredit,
      closeButtonText: "Fechar",
    });
  };

  useEffect(() => {
    fetchSalesCoupons();
    if (currenCreditCardId !== undefined && currenCreditCardId !== "pix") {
      setIsLoadingCardInfo(true);
      (async () => {
        try {
          const response = await getUserPaymentMethodById(currenCreditCardId);
          setIsLoadingCardInfo(false);
          if (response.id) {
            setCardInfo(response);
          }
        } catch (er) {
          setIsLoadingCardInfo(false);
          console.log(er);
        }
      })();
    }
  }, []);

  useEffect(() => {
    setHeaderConfig({
      headerText: "Adicionar crédito",
      // backNavigation: () => navigate(`/${brandContext.brandName}`),
    });
    return () => setHeaderConfig({});
  }, [brandContext.brandName, navigate, setHeaderConfig]);

  if (isCheckingCpf) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <LoaderSecondary className="spinner" />
      </div>
    );
  }

  if (hasRedirectedForCpf) {
    return null;
  }

  return (
    <>
      {currenCreditCardId && currenCreditCardId !== "pix" && (
        <div className="add-credit-content-container">
          <Button
            style={{
              marginBottom: "0px",
              color: "var(--Primary-Regular, #3CCC10)",
              backgroundColor: "#fff",
              border: "1px solid #3CCC10",
            }}
            type="button"
            children={
              <span>
                {cardInfo &&
                  Object.keys(cardInfo).length > 0 &&
                  getTypeCard(cardInfo?.item_type)}{" "}
                - <b>{cardInfo?.description}</b>
              </span>
            }
            spinnerSecondary={true}
            loading={isLoadingCardInfo}
            image={<img alt="add-button-icon" src={checkGreen} />}
          />
        </div>
      )}
      <div
        className="add-credit-options-container"
        style={{ paddingBottom: 100 }}
      >
        <b>Adicione créditos</b>
        <p>Qual valor você deseja adicionar?</p>
        <div className="add-credit-input-divider"></div>
        {presetValues.map((value: number, index: number) => (
          <div key={index} className="add-credit-input-container">
            <Input
              labelName={`R$ ${value}`}
              type="radio"
              id={String(index)}
              value={value}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSelectedValue(Number(event?.target?.value));
          setSelectedCouponId(undefined);
          setOtherValue(null);
              }}
              checked={selectedValue === value && !selectedCouponId}
            />
            <div className="add-credit-input-divider"></div>
          </div>
        ))}
        {cashAmountsCoupons.map((item: any) => (
          <div key={item.dcId} className="add-credit-input-container">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "-10px 0",
              }}
            >
              <div className="nowrap" style={{ width: 130 }}>
                <Input
                  labelName={`R$ ${new Intl.NumberFormat("pt", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(item.dcValueVoucher)}`}
                  type="radio"
                  id={item.dcId}
                  value={item.dcValueVoucher ?? 0}
                  onChange={(event) => {
                    setSelectedValue(Number(event?.target?.value));
                    setSelectedCouponId(item.dcId);
                    setOtherValue(null);
                  }}
                  checked={selectedCouponId === item.dcId}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "12px",
                  lineHeight: 1.1,
                }}
                onClick={() => handleCouponDetail(item)}
              >
                <div style={{ paddingTop: 3, marginRight: 5 }}>
                  <img
                    width={50}
                    src={couponIcon}
                    alt="ticket"
                    style={{ filter: "brightness(.9)" }}
                  />
                </div>
                <div>
                  + Cupom de
                  <div style={{ fontSize: "20px" }}>
                    {item.dcParameter}%{" "}
                    <span
                      style={{
                        textTransform: "uppercase",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      Off
                    </span>
                  </div>
                  nas recargas
                </div>
              </div>
              <div
                style={{
                  paddingTop: 3,
                  width: 60,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircleAlert
                  color="#383838"
                  width={24}
                  height={24}
                  onClick={() => handleCouponDetail(item)}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
            <div className="add-credit-input-divider"></div>
          </div>
        ))}
        {allowCustomValue && (
          <div className="add-credit-input-container">
            <Input
              labelName={`Outro valor`}
              type="radio"
              id="secondName"
              value={"other"}
              onChange={(event) => {
                setSelectedValue(event?.target?.value);
                setSelectedCouponId(undefined);
                setOtherValue("");
              }}
              checked={selectedValue === "other"}
            />
            {selectedValue === "other" && (
              <div style={{ padding: "10px 25px 0" }}>
                <Input
                  htmlFor="otherValue"
                  type="tel"
                  id="otherValue"
                  className={
                    errorsValidate.otherValue
                      ? "input-pattern error-input fontSize"
                      : "input-pattern fontSize"
                  }
                  placeholder={`Ex: R$ ${minValue}`}
                  value={otherValue}
                  onChange={(event) => {
                    setErrorsValidate({ otherValue: false });
                    const raw = event.target.value.replace(/\D/g, "");
                    if (!raw) {
                      setOtherValue("");
                      return;
                    }
                    const numericValue = parseFloat(raw) / 100;
                    
                    // Validar minValue e maxValue
                    let hasError = false;
                    if (numericValue < minValue) {
                      hasError = true;
                    }
                    if (numericValue > maxValue) {
                      hasError = true;
                    }
                    
                    setErrorsValidate({
                      ...errorsValidate,
                      otherValue: hasError,
                    });
                    const formatted = new Intl.NumberFormat("pt", {
                      style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(numericValue);

                  setOtherValue(formatted);
                }}
                isErrorValidate={errorsValidate.otherValue ? true : false}
                messageError={
                  errorsValidate.otherValue
                    ? `Valor deve estar entre R$ ${minValue} e R$ ${maxValue}`
                    : ""
                }
              />
              <div style={{ fontSize: "14px", marginTop: "-10px" }}>
                Valor mínimo de R$ {minValue}, máximo de R$ {maxValue}
              </div>
            </div>
          )}
          <div className="add-credit-input-divider"></div>
        </div>
        )}
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            backgroundColor: "#fff",
            padding: "10px 20px 35px",
            boxSizing: "border-box",
          }}
        >
          <Button
            type="button"
            children="Adicionar Créditos"
            loading={isLoading}
            disabled={isLoading || isCpfMissing}
            onClick={handleToastConfirmCredid}
            style={{
              marginTop: "10px",
              backgroundColor: brandContext.brandBgColor,
            }}
            image={
              <img
                width="20px"
                height="20px"
                alt="add-button-icon"
                src={addCircle}
              />
            }
          />
        </div>
      </div>
      <Modal
        allowButtonClosed={false}
        isOpen={showCouponInfoModal}
        onClose={() => setShowCouponInfoModal(false)}
        title={() => {
          return (
            <div
              style={{
                display: "flex",
                textAlign: "left",
                padding: "0 12px",
                alignItems: "center",
              }}
            >
              <div>{titleCouponInfoModal}</div>
              <div style={{ margin: 20 }}>
                <CircleAlert width={40} height={40} />
              </div>
            </div>
          );
        }}
        styleEnterButton={{
          backgroundColor: brandContext.brandBgColor,
          border: "none",
        }}
        textEnterButton="OK, entendi"
        handleEnterButton={() => setShowCouponInfoModal(false)}
        isLoadingEnterButton={isLoading}
      >
        <p>{textCouponInfoModal}</p>
      </Modal>
    </>
  );
};

export default AddCredit;
